import type { ShippingPriceResult } from "@/api/ShippingApi";

/**
 * **배송 가능 상한(부피무게 kg) — ★우리 제한이다.**
 *
 * GE 가 준 값이 아니다. 표 이름이 `글로벌e 운반비` 라서 GE 제한처럼 읽히기 쉬운데, 그 표는
 * **우리 DHL·UPS 요금표로 우리가 만든 것**이다(물류 소유). 그래서 이 상한은 결제 경로와
 * 무관하게 「우리가 보낼 수 있는가」다.
 *
 * 물류 요율표(`글로벌e 운반비`)의 마지막 구간이 `35,600~36,099g` 이라, 이 값을 넘으면
 * **요율표에 값이 없다**. 그런데 mgmt `calculate-by-sku` 는 상한 밖에도 가격을 돌려준다
 * (2026-08-28 실측: 36.5kg → $124, 39.5kg → $134). 즉 **배송할 수 없는 주문이 결제까지
 * 통과**한다.
 *
 * ## ★부피무게 기준이다 (실중량 아님) — 2026-09-10 원본 대조 완료
 *
 * 판정에 쓰는 `detail.weight` 는 mgmt 가 계산한 **부피무게**다. 같은 카트의 제품 실중량 합과
 * 크게 다르다 — Pro×4 + Air×4 는 실중량 44kg 인데 부피무게는 **36.5kg** 이다.
 *
 * **요율표 원본을 확인했다**(`글로벌e 운반비 초안_v260813.xlsx`, 물류 소유 · 최신판).
 * `bd6.부피무게` 시트가 SKU 별 「최초1개 / 추가1개 부피무게」를 정의하고, `가격표(전체)` 시트의
 * 마지막 구간이 `35,600~36,099` 다 → **36,099g 초과는 요율표에 값이 없다**.
 *
 * ⇒ **축은 같다**(둘 다 부피무게 · SKU 별 「최초/추가」 모델). 실중량으로 바꾸면 요율표와
 * 어긋나 멀쩡한 주문이 막힌다.
 *
 * ### ★두 층을 갈라야 한다 — 입력 vs 천장
 *
 * | 층       | 무엇                         | 정본                                     |
 * | -------- | ---------------------------- | ---------------------------------------- |
 * | **입력** | 이 카트가 몇 g 인가          | **mgmt `detail.weight`**(2026-09-10 확정) |
 * | **천장** | 요율표가 값을 가진 최대 무게 | `가격표(전체)` 마지막 구간 `36,099g`     |
 *
 * `가격표(전체)` 구간은 **무게를 받아 가격을 찾는 표**라 SKU 별 무게와 무관하다.
 * `bd6.부피무게`(SKU 별 최초/추가)는 입력을 만드는 쪽이고, 정본이 mgmt 이므로 그 시트는
 * 물류 내부 계획용 사본이다.
 *
 * ⚠️ **xlsx `bd6` 값이 mgmt 와 다르다**(2026-09-10 prod 전수 실측 — 7개 군 중 2개만 일치.
 * AIR `6,000/4,500` vs prod `5,000/4,000` 등). 전사 오류가 아니라 원본이 그렇게 적혀 있다.
 * **우리 게이트에는 영향이 없다** — 입력이 mgmt 이므로. 상세는
 * `docs/global-e-storefront.md` §4 flag ON 게이트.
 *
 * ★**초판 주석은 이 차이를 「게이트가 새는 방향」으로 읽었는데 틀렸다.** 카트 무게는 mgmt 가
 * 말하는 값이 곧 사실이고, 36.1kg 이하면 요율표가 가격을 낼 수 있으니 보낼 수 있다.
 * 「입력의 정본」과 「천장의 출처」가 다른 파일에 있다는 사실을 **둘이 어긋난다**로 읽은 것이다.
 *
 * ## ★FE 상수인 이유와 이관 계획
 *
 * 요율표는 물류가 관리하고 mgmt 가 그걸로 계산한다. 원칙적으로는 **mgmt 가 판정해 내려주는
 * 것이 단일 출처**지만(요율표 개정 시 한 곳만 고치면 된다), 그 작업 전까지 상한이 열려 있는
 * 상태를 두지 않으려고 FE 에 둔다.
 *
 * ⚠️ **요율표가 바뀌면 이 값도 같이 고칠 것.** mgmt 가 `OVERWEIGHT` 판정을 응답에 실어주면
 * 이 상수를 지우고 그 신호를 쓰면 된다. 이 결합은 `docs/global-e-storefront.md` §4 의
 * flag ON 게이트에도 적어 뒀다 — 요율표를 만지는 쪽에서 소스 주석은 안 보인다.
 *
 * ## ★2026-09-17 — 술어가 `>` 에서 `>=` 로 바뀌었다 (1g 간극 폐쇄)
 *
 * 종전 `weight > 36.1` 은 **정확히 36,100g 을 통과**시켰고, 그 간극을 *"물류 기준이
 * 「36.1kg 초과 차단」이라 36.099 로 바꾸면 코드가 정책보다 엄격해진다"* 는 이유로 뒀다.
 *
 * **그 판단은 틀렸다.** GE 에 준 운반비 초안의 구간이 드러나면서 36,100 이 안전한 값이
 * 아니라는 것이 확인됐다:
 *
 * | 구간 | 가격 |
 * | --- | --- |
 * | 35,600 ~ **36,099** | 정상 요율 |
 * | **36,100** ~ 200,000 | **850,000,000** (배송 불가 sentinel) |
 *
 * 즉 36,100g 은 「상한」이 아니라 **이미 절벽 너머**다. `>=` 로 막는 것은 정책보다 엄격한
 * 것이 아니라 **정책을 정확히 구현**하는 것이다 — 요율표가 값을 가진 마지막 무게는 36,099g 이다.
 *
 * ⚠️ 상수 자체는 `36.1` 을 유지한다. 「요율표에 값이 없어지는 첫 무게」라는 의미가 그대로이고,
 * 36.099 로 바꾸면 요율표 개정 때 대조할 숫자가 오히려 흐려진다.
 *
 * ★**이 게이트만으로는 안 닫힌다** — 우리가 통과시킨 36,099g 카트를 GE 가 더 무겁게 계산하면
 * 다시 절벽을 넘는다. 그래서 `get-checkout-cart-info.ts` 가 라인 무게를 **소수 2자리**로 보내
 * GE 합계와의 오차를 구간 단위(1g)보다 100배 아래로 낮춘다(#5793). 두 장치가 짝이다.
 *
 * 번들 카트도 짝이 맞는다 — 이 게이트의 입력(`api/shops/shipping-price-by-sku`)과 GE 라우트가 둘 다
 * **번들 SKU 그대로** 묻는다(#5791 1번, 2026-10-08). 그 전엔 이쪽만 `skuOption.skus` 로 전개해서
 * 글러브 올사이즈 번들 4종에서 여기 6.0kg · GE 4.5kg 로 갈렸다(게이트가 더 엄격한 쪽이라 우연히 안전).
 */
export const GE_MAX_SHIPPING_WEIGHT_KG = 36.1;

/**
 * **이 카트가 배송 상한을 넘었는가** — 카트·바로구매가 공유하는 단일 술어.
 *
 * ★`geRouted` 를 인자로 받아 **GE 경로에서만** 판정한다.
 *
 * ⚠️ 근거는 「되돌리기 비용」이 **아니다**(초판 주석이 그렇게 적었는데 틀렸다 — 상한이 우리
 * 제한이므로 자체 결제로 들어와도 못 보내고 결국 취소해야 한다).
 *
 * 진짜 근거: **flag ON 이면 국제 주문이 전부 GE 를 탄다.** `runCheckout` 의 GE 분기가
 * 비-KR + flag ON 을 전부 먼저 반환하므로, 그 뒤 자체 결제에 도달하는 것은 **KR 또는
 * flag OFF** 뿐이다. ⇒ GE 경로만 막으면 그 시점에 **국제 전건 커버**다.
 *
 * flag OFF(현재 prod)에서 발화하지 않는 것은 **출시 전 과도 상태**이고 영구 구멍이 아니다.
 * 그 구간에 국제 주문이 자체 결제로 들어오지만, 지금까지는 수량 상한(SKU 당 4개)이 대부분
 * 막아준다 — **요율표 시트에 열거된 단품 기준**으로 한 종류 ×4 는 최대 19.5kg(PRO)이고
 * **두 종류를 섞어야** 36.1kg 을 넘는다.
 *
 * ⚠️ 「한 종류」의 모집단이 **요율표 시트의 단품**이다. 번들은 번들 SKU 그대로 번들 무게로 계산된다
 * (#5791 1번 이후) — prod 번들 20개의 1개 무게는 최대 7.0kg(`fps-bundle-tactvisor`, 2026-10-08)이라
 * 한 종류 ×4 는 28kg 을 넘지 않는다(1개 무게 ×4 기준 상한 — 「추가 1개」 무게는 더 가볍다).
 *
 * ★flag ON 후에도 게이트 밖에 남는 두 경로(둘 다 의도된 제외, 2026-09-10 확인):
 *   · **한국(내수)** — 이 상한은 국제 배송 기준이다
 *   · **admin·인플루언서 draft**(`checkout/?id=`) — 자체 결제 + 배송비 포함이라 사람이 판단한다
 *
 * ★무게를 **모르면 막지 않는다**(`undefined`/`null`). 배송비 조회 실패·게임 단품 카트(배송비
 * 쿼리 자체가 안 돎)까지 막으면 멀쩡한 구매가 차단된다. 판정 실패는 통과시키고, 배송비
 * 조회 실패는 **기존 `shippingFailed` 게이트**가 따로 잡는다(자체 결제 경로 한정).
 */
export function exceedsGeShippingWeight({
  geRouted,
  shipping,
}: {
  geRouted: boolean;
  shipping: ShippingPriceResult | null | undefined;
}): boolean {
  if (!geRouted) return false;
  const weight = shipping?.detail?.weight;
  if (typeof weight !== "number" || !Number.isFinite(weight)) return false;
  return weight >= GE_MAX_SHIPPING_WEIGHT_KG;
}

/**
 * **무게 판정이 아직 안 끝났는가** — GE 경로에서 체크아웃 진입을 잠시 막아야 하는 구간.
 *
 * ★「무게를 알 수 없다」와 「아직 모른다」는 **다른 상태**다.
 *
 * - **알 수 없다**(`enabled === false`: 게임 단품 카트 · countryCode 미확정 / 조회 실패 `null`)
 *   → **통과**. `exceedsGeShippingWeight` 가 `false` 를 돌려주는 것과 같은 판단이다.
 * - **아직 모른다**(조회 중 · 조회 시작 전) → **대기**. 여기서 통과시키면 37.5kg 카트도
 *   페이지 로드 직후엔 버튼이 살아 있어 그대로 GE 로 넘어간다.
 *
 * 이 구간이 예외가 아니라 **기본값**이라는 점이 함정이다. `/cart` 첫 진입과 **수량 변경 직후**
 * (`list` 가 queryKey 에 있어 새 키 → `data === undefined`, `placeholderData` 없음)가 전부
 * 여기 해당한다.
 *
 * 바로구매(`runCheckout`)는 매번 새로 조회하고 `await` 하므로 이 대기가 필요 없다 —
 * **두 경로가 같은 상한 술어를 공유하지만 데이터 신선도 계약이 다르다.**
 */
export function isGeShippingWeightPending({
  geRouted,
  enabled,
  isSuccess,
  isError,
}: {
  geRouted: boolean;
  enabled: boolean;
  isSuccess: boolean;
  isError: boolean;
}): boolean {
  if (!geRouted) return false;
  // 조회가 돌지 않는 카트는 무게를 영영 알 수 없다 → 대기가 아니라 통과.
  // ⚠️ TanStack Query v4+ 는 `enabled: false` 를 `status: 'pending'` 으로 **영원히** 남긴다.
  if (!enabled) return false;
  // 성공(`data === null` 포함)·실패로 결론이 났으면 대기 끝.
  return !isSuccess && !isError;
}
